/**
 * GravBase Stats Counter
 * File: assets/anj-stats-counter.js
 * Section: sections/anj-stats-counter.liquid
 *
 * Counts every number in the section up from zero the first time the section
 * scrolls into view. The page already holds the final numbers, so without
 * JavaScript, or for search engines, nothing is missing.
 *
 * A number in live mode shows an estimate: its start number plus the real daily
 * rate for every day after its start date, never for more days than the cap, so
 * a start number left alone for months stops growing instead of running ahead of
 * the store. The estimate is redrawn at random moments a few seconds apart; the
 * randomness only decides when it is redrawn, never what it says.
 *
 * Visitors who ask for reduced motion get the numbers without the count-up.
 *
 * Test mode ticks every number up by a set amount at a randomised pace so the
 * change animation can be tested. It needs data-test-step, which the section
 * writes only in the theme editor, and Shopify.designMode, which is only true
 * there; on the live storefront it cannot start.
 */
(function () {
  'use strict';

  const DAY_MS = 86400000;
  const REDRAW_MIN_MS = 4000;
  const REDRAW_SPREAD_MS = 6000;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toNumber(value) {
    const n = parseFloat(String(value || '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }

  // Ease out: quick at first, settling gently onto the final number.
  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /** One number in the section. */
  class Stat {
    constructor(el, locale) {
      this.el = el;
      this.out = el.querySelector('[data-stat-value]');
      this.sizer = el.querySelector('.anj-stats__sizer');
      this.affix = Math.max(0, Math.floor(toNumber(el.dataset.affix)));
      this.base = Math.max(0, Math.floor(toNumber(el.dataset.value)));
      this.rate = Math.max(0, toNumber(el.dataset.rate));
      // The start number was true on the start date, so counting begins at the
      // end of that day (UTC). Entering it in the morning undercounts, never over.
      this.since = Date.parse(el.dataset.since || '') + DAY_MS;
      this.capDays = Math.max(0, toNumber(el.dataset.capDays));
      this.live = el.dataset.live === 'true' && this.rate > 0 && Number.isFinite(this.since);
      this.format = new Intl.NumberFormat(locale, el.dataset.format === 'compact'
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { maximumFractionDigits: 0 });
      this.shown = this.base;
      this.frame = 0;
      this.timer = 0;
    }

    /** The number to show now: the start number, plus the daily rate in live mode. */
    target(now) {
      if (!this.live) return this.base;
      const days = Math.min(Math.max(0, (now - this.since) / DAY_MS), this.capDays);
      return Math.floor(this.base + this.rate * days);
    }

    render(value) {
      this.shown = value;
      if (this.out) this.out.textContent = this.format.format(value);
    }

    /** Count from what is showing to `to` over `duration` ms; straight there if 0. */
    countTo(to, duration) {
      cancelAnimationFrame(this.frame);
      const from = this.shown;
      if (!duration || to === from) {
        this.render(to);
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        this.render(Math.round(from + (to - from) * easeOut(t)));
        if (t < 1) this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    /** Hold the width of the number it will end on, so counting never shifts it. */
    fit(to) {
      const text = this.format.format(to);
      if (this.sizer) this.sizer.textContent = text;
      this.chars = text.length + this.affix;
      if (this.onFit) this.onFit();
    }

    start(duration) {
      const to = this.target(Date.now());
      this.fit(to);
      this.countTo(to, duration);
      if (this.live) this.schedule();
    }

    /** Redraw a live number every few seconds; it only moves when the estimate has. */
    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.redraw();
        this.schedule();
      }, REDRAW_MIN_MS + Math.random() * REDRAW_SPREAD_MS);
    }

    redraw() {
      const to = this.target(Date.now());
      if (to <= this.shown) return;
      this.fit(to);
      this.countTo(to, reducedMotion() ? 0 : 800);
    }

    /** Theme editor testing: add `step` every `seconds` on average, 50 to 150 percent of it. */
    test(step, seconds) {
      this.live = false;
      clearTimeout(this.timer);
      // Its own running total, so ticks still add up while the browser holds
      // back animation frames, for example in a background tab.
      let goal = this.shown;
      const tick = () => {
        this.timer = setTimeout(() => {
          goal += step;
          this.fit(goal);
          this.countTo(goal, reducedMotion() ? 0 : 600);
          tick();
        }, seconds * 1000 * (0.5 + Math.random()));
      };
      tick();
    }

    stop() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
    }
  }

  class AnjStatsCounter extends HTMLElement {
    connectedCallback() {
      const locale = document.documentElement.lang || undefined;
      this.stats = Array.from(this.querySelectorAll('[data-stat]')).map((el) => new Stat(el, locale));
      if (!this.stats.length) return;

      // Every number shrinks by the longest one, so a row stays one size.
      const grid = this.querySelector('.anj-stats__grid');
      const sizeRow = () => {
        const longest = Math.max(1, ...this.stats.map((stat) => stat.chars || 0));
        if (grid) grid.style.setProperty('--anj-stats-chars', String(longest));
      };
      this.stats.forEach((stat) => { stat.onFit = sizeRow; });

      const duration = Math.max(0, toNumber(this.dataset.duration)) * 1000;

      // Test mode: only in the theme editor, never on the live storefront.
      const testStep = Math.floor(toNumber(this.dataset.testStep));
      const testSeconds = Math.max(1, toNumber(this.dataset.testSeconds));
      const testing = testStep > 0 && !!(window.Shopify && window.Shopify.designMode);
      const startTests = () => {
        if (testing) this.stats.forEach((stat) => stat.test(testStep, testSeconds));
      };
      const countUp = this.dataset.animate === 'true' && !reducedMotion() && duration > 0 &&
        'IntersectionObserver' in window;

      if (!countUp) {
        this.stats.forEach((stat) => stat.start(0));
        startTests();
      } else {
        // Hold at zero until the section is on screen, then count up once.
        this.stats.forEach((stat) => stat.render(0));
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.stats.forEach((stat) => stat.start(duration));
          if (testing) setTimeout(startTests, duration);
        }, { threshold: 0.3 });
        this.observer.observe(this);
      }

      // Live numbers rest while the tab is hidden and catch up when it is back.
      this.onVisibility = () => {
        this.stats.forEach((stat) => {
          if (!stat.live || !stat.timer) return;
          if (document.hidden) {
            clearTimeout(stat.timer);
          } else {
            stat.redraw();
            stat.schedule();
          }
        });
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      (this.stats || []).forEach((stat) => stat.stop());
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  if (!customElements.get('anj-stats-counter')) {
    customElements.define('anj-stats-counter', AnjStatsCounter);
  }
})();
